import Config from '../config/Config';
import store from '../redux';
import { readPersistedEditionId } from './editionPrefs';
import { createRequestId } from './requestId';
import { buildRequestInstanceHeaders } from './requestInstance';
import { errorFromNetworkFailure, withSafeJson } from './rest/getApiErrorMessage';

function append_query_pairs(params, value, key) {
    if (value === undefined || value === null) {
        return;
    }

    if (Array.isArray(value)) {
        // PHP parse_str splits `key[][a]=1&key[][b]=2` into two incomplete
        // objects. Index object items so nested pairs stay together
        // (`key[0][a]=1&key[0][b]=2`). Scalar arrays keep `key[]=…`.
        value.forEach((item, index) => {
            const is_object_item = item !== null
                && typeof item === 'object'
                && !Array.isArray(item);
            append_query_pairs(
                params,
                item,
                is_object_item ? `${key}[${index}]` : `${key}[]`
            );
        });
        return;
    }

    if (typeof value === 'object') {
        Object.keys(value).forEach((child_key) =>
            append_query_pairs(params, value[child_key], `${key}[${child_key}]`)
        );
        return;
    }

    params.append(key, String(value));
}

function build_query_string(query) {
    // Cache-stable query: nocache first (nginx strip), remaining keys alphabetical.
    const src = { ...(query || {}) };
    const add_nocache =
        src.nocache === true || src.nocache === 'true' || src.nocache === 1 || src.nocache === '1';
    delete src.nocache;

    const params = new URLSearchParams();
    Object.keys(src)
        .sort()
        .forEach((key) => {
            append_query_pairs(params, src[key], key);
        });

    const rest = params.toString();
    if (add_nocache) {
        return rest ? `nocache=true&${rest}` : 'nocache=true';
    }
    return rest;
}

function headers_to_object(headers) {
    const out = {};
    if (!headers || typeof headers.forEach !== 'function') {
        return out;
    }
    headers.forEach((value, key) => {
        out[key] = value;
    });
    return out;
}

/**
 * Shared GET state: in-flight promise dedupe + ETag body replay on 304.
 * Keyed by absolute URL + API key + instance (headers vary the body).
 */
const get_inflight = new Map();
const get_body_cache = new Map();
const GET_BODY_CACHE_MAX = 80;

/** @internal Test helper — clears in-flight + ETag body caches. */
export function reset_client_get_caches() {
    get_inflight.clear();
    get_body_cache.clear();
}

function get_cache_key(url, headers) {
    const api_key = headers['X-API-Key'] || headers['x-api-key'] || '';
    const instance =
        headers['X-Tkt-Instance-Id'] ||
        headers['x-tkt-instance-id'] ||
        '';
    return `${url}\n${api_key}\n${instance}`;
}

function remember_get_body(cache_key, etag, body_text, status, header_obj) {
    if (!etag || body_text === undefined) {
        return;
    }
    if (get_body_cache.size >= GET_BODY_CACHE_MAX) {
        const first = get_body_cache.keys().next().value;
        get_body_cache.delete(first);
    }
    get_body_cache.set(cache_key, {
        etag,
        body_text,
        status,
        headers: header_obj,
    });
}

function response_from_cache(entry, etag_header) {
    const headers = {
        ...(entry.headers || {}),
        'Content-Type': entry.headers?.['content-type']
            || entry.headers?.['Content-Type']
            || 'application/json',
    };
    if (etag_header || entry.etag) {
        headers.ETag = etag_header || entry.etag;
    }
    return new Response(entry.body_text, {
        status: entry.status || 200,
        statusText: 'OK',
        headers,
    });
}

export default class Client {
    constructor() {
        this.baseUrl = Config.api_end_point;
        this.headers = {
            'Accept': 'application/json',
        };
    }

    should_inject_edition(route_path) {
        if (!route_path || typeof route_path !== 'string') {
            return true;
        }
        // Never scope edition admin endpoints themselves.
        if (route_path.startsWith('/edition')) {
            return false;
        }
        // Engine cron tasks (GET /cron_task/list → tasks collection) are global:
        // no edition field; passing ?edition=… makes Mongo return nothing.
        if (route_path.startsWith('/cron_task')) {
            return false;
        }
        return true;
    }

    /**
     * Active edition for API scoping: Redux first, then prefs (localStorage)
     * so F5 never races bare monoid GETs before the store finishes hydrating.
     */
    resolve_edition_id() {
        const from_store = store.getState().editionNavigation?.selectedEditionId;
        if (from_store != null && String(from_store) !== '') {
            return String(from_store);
        }
        const from_prefs = readPersistedEditionId();
        return from_prefs != null && String(from_prefs) !== ''
            ? String(from_prefs)
            : null;
    }

    /**
     * Monoid settings.edition (instance active edition). When selected edition
     * equals this, omit ?edition= so nginx keys match mute clients (eshop/WP).
     */
    resolve_monoid_edition_id() {
        const from_settings = store.getState().settings?.setting?.edition;
        if (from_settings != null && String(from_settings).trim() !== '') {
            return String(from_settings).trim();
        }
        return null;
    }

    /**
     * Whether API calls should add ?edition= for the resolved selection.
     */
    edition_query_for_path(route_path) {
        if (!this.should_inject_edition(route_path)) {
            return null;
        }
        const selected = this.resolve_edition_id();
        if (!selected) {
            return null;
        }
        const monoid = this.resolve_monoid_edition_id();
        if (monoid && selected === monoid) {
            return null;
        }
        return selected;
    }

    /**
     * Deduped GET with If-None-Match when a previous body is known.
     * Identical concurrent GETs share one network call.
     */
    _fetch_get(url, options) {
        const cache_key = get_cache_key(url, options.headers || {});
        const existing = get_inflight.get(cache_key);
        if (existing) {
            return existing.then((entry) => response_from_cache(entry));
        }

        const headers = { ...(options.headers || {}) };
        const cached = get_body_cache.get(cache_key);
        if (cached?.etag) {
            headers['If-None-Match'] = cached.etag;
        }

        const promise = fetch(url, { ...options, headers })
            .then(async (response) => {
                if (response.status === 304 && cached) {
                    const entry = {
                        etag: response.headers.get('ETag') || cached.etag,
                        body_text: cached.body_text,
                        status: 200,
                        headers: cached.headers,
                    };
                    remember_get_body(
                        cache_key,
                        entry.etag,
                        entry.body_text,
                        entry.status,
                        entry.headers
                    );
                    return entry;
                }

                // 304 without a local body: re-fetch without precondition.
                if (response.status === 304) {
                    const retry_headers = { ...headers };
                    delete retry_headers['If-None-Match'];
                    const retry = await fetch(url, {
                        ...options,
                        headers: retry_headers,
                    });
                    const body_text = await retry.text();
                    const etag = retry.headers.get('ETag');
                    const header_obj = headers_to_object(retry.headers);
                    const entry = {
                        etag,
                        body_text,
                        status: retry.status,
                        headers: header_obj,
                    };
                    if (retry.ok && etag) {
                        remember_get_body(
                            cache_key,
                            etag,
                            body_text,
                            retry.status,
                            header_obj
                        );
                    }
                    return entry;
                }

                const body_text = await response.text();
                const etag = response.headers.get('ETag');
                const header_obj = headers_to_object(response.headers);
                const entry = {
                    etag,
                    body_text,
                    status: response.status,
                    headers: header_obj,
                };
                if (response.ok && etag) {
                    remember_get_body(
                        cache_key,
                        etag,
                        body_text,
                        response.status,
                        header_obj
                    );
                }
                return entry;
            })
            .finally(() => {
                get_inflight.delete(cache_key);
            });

        get_inflight.set(cache_key, promise);
        return promise.then((entry) => response_from_cache(entry));
    }

    _fetch(path, method, query, body = null, extraHeaders = {}) {
        const editionId = this.edition_query_for_path(path);
        const has_explicit_edition = query != null
            && Object.prototype.hasOwnProperty.call(query, 'edition')
            && query.edition != null
            && String(query.edition).trim() !== '';
        if (editionId && !has_explicit_edition) {
            query = { ...(query || {}), edition: String(editionId) };
        }

        let url     = `${this.baseUrl}${path}`;
        if (query) {
            const q = build_query_string(query);
            if (q) {
                url = `${url}${url.includes('?') ? '&' : '?'}${q}`;
            }
        }

        let user = store.getState().auth.user;
        if (user && user.api_key)
            extraHeaders['X-API-Key'] = user.api_key;

        const instanceHeaders = buildRequestInstanceHeaders(path);
        const request_id = createRequestId();
        this.last_request_id = request_id;
        this.last_request_meta = {
            method,
            path,
            requestId: request_id,
            instance: instanceHeaders['X-Tkt-Instance-Slug'] || null,
        };
        let options = {
            method,
            headers: {
                ...this.headers,
                'X-Request-ID': request_id,
                ...instanceHeaders,
                ...extraHeaders,
            }
        };

        if (body) {
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(body);
        }

        const fail = (error) => {
            throw errorFromNetworkFailure(error, request_id);
        };

        const wrap = (response) => withSafeJson(response, request_id, {
            ...this.last_request_meta,
            status: response?.status,
        });

        if (method === 'GET') {
            return this._fetch_get(url, options)
                .then(wrap, fail);
        }

        // Writes invalidate ETag body cache so the next GET never 304-replays
        // a pre-mutation payload (pricinglist rename / settings save / …).
        return fetch(url, options).then((response) => {
            if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
                reset_client_get_caches();
            }
            return wrap(response);
        }, fail);
    }

    GET(route, query, extraHeaders = {}) {
        return this._fetch(route, 'GET', query, null, extraHeaders);
    }

    POST(route, body, query, extraHeaders = {}) {
        return this._fetch(route, 'POST', query, body, extraHeaders);
    }

    PUT(route, body, extraHeaders = {}) {
        return this._fetch(route, 'PUT', null, body, extraHeaders);
    }

    DELETE(route, query, extraHeaders = {}) {
        return this._fetch(route, 'DELETE', query, null, extraHeaders);
    }

    POSTMultipart(route, form_data, query, extraHeaders = {}) {
        const editionId = this.edition_query_for_path(route);
        const has_explicit_edition = query != null
            && Object.prototype.hasOwnProperty.call(query, 'edition')
            && query.edition != null
            && String(query.edition).trim() !== '';
        if (editionId && !has_explicit_edition) {
            query = { ...(query || {}), edition: String(editionId) };
        }

        let url = `${this.baseUrl}${route}`;
        if (query) {
            const q = build_query_string(query);
            if (q) {
                url = `${url}${url.includes('?') ? '&' : '?'}${q}`;
            }
        }

        const user = store.getState().auth.user;
        if (user?.api_key)
            extraHeaders['X-API-Key'] = user.api_key;

        const instanceHeaders = buildRequestInstanceHeaders(route);
        const request_id = createRequestId();
        this.last_request_id = request_id;
        this.last_request_meta = {
            method: 'POST',
            path: route,
            requestId: request_id,
            instance: instanceHeaders['X-Tkt-Instance-Slug'] || null,
        };
        return fetch(url, {
            method: 'POST',
            headers: {
                ...this.headers,
                'X-Request-ID': request_id,
                ...instanceHeaders,
                ...extraHeaders,
            },
            body: form_data,
        }).then((response) => {
            reset_client_get_caches();
            return withSafeJson(response, request_id, {
                ...this.last_request_meta,
                status: response?.status,
            });
        }, (error) => {
            throw errorFromNetworkFailure(error, request_id);
        });
    }

    /**
     * Last X-Request-ID issued by this client (for error messages).
     * @returns {string|undefined}
     */
    get_last_request_id() {
        return this.last_request_id;
    }

    /**
     * Metadata for the last request (Yoda client-error reporting).
     * @returns {object|undefined}
     */
    get_last_request_meta() {
        return this.last_request_meta;
    }
};
